/**
 * CobrandVanilaCarousel Molecule Component
 *
 * A carousel component using Glide.js for displaying credit card offers and content.
 * Supports multiple responsive breakpoints, RTL layouts, and various visual variations.
 *
 * Key Features:
 * - Glide.js integration for smooth carousel functionality
 * - Responsive breakpoints (mobile, tablet, desktop)
 * - RTL (Right-to-Left) support
 * - Custom arrow controls with tracking
 * - Combo and standard variations
 * - Performance optimizations with React hooks
 *
 * @module molecules/CobrandVanilaCarousel
 */

// External library imports
import { FC, useEffect, useState, useRef, useCallback, useMemo, memo, type RefObject } from 'react';
import clsx from 'clsx';
import Glide from '@glidejs/glide';

// Internal imports
import { VanilaCardCarouselContainerProps, CardsPerView } from './CobrandVanilaCarousel.types';
import { StyledCardCarousel } from './CobrandVanilaCarousel.styles';
import { MButton, MButtonType, MButtonIconPlacement } from '@marriott/mi-web-ui-kit';
import { Eyebrow, Heading, Text, headingType, size, tags, useCheckBreakpoint } from '@marriott/mi-ui-library';

// ============================================================================
// Constants
// ============================================================================

/**
 * Responsive breakpoint configuration for Glide.js
 */
const BREAKPOINTS = {
  MOBILE_MAX: 575,
  TABLET_MIN: 576,
  TABLET_MAX: 767,
  DESKTOP_MIN: 768,
  LARGE_DESKTOP_MIN: 1200,
} as const;

/**
 * Glide.js carousel configuration defaults
 */
const GLIDE_CONFIG = {
  TYPE: 'slider',
  BOUND: true,
  START_AT: 0,
  REWIND: false,
  GAP: 24,
  GAP_MOBILE: 16,
  DRAG_THRESHOLD: false,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Creates Glide.js arrow disabler extension
 * Manages arrow button disabled states based on carousel position
 *
 * @param isDirectionRightToLeft - Whether RTL mode is active
 */
const createArrowDisabler = (isDirectionRightToLeft: boolean) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function ArrowDisabler(GlideInstance: any, Components: any) {
    return {
      mount() {
        if (GlideInstance.settings.rewind) return;

        GlideInstance.on(['mount.after', 'run'], () => {
          const isAtStart = GlideInstance.index === 0;
          const slidesLength = Components.Sizes?.length || 0;
          const perView = GlideInstance.settings?.perView || 1;
          const isAtEnd = slidesLength > 0 ? GlideInstance.index === slidesLength - perView : false;

          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') continue;

            const left = controlItem?.querySelector('.glide__arrow--left');
            const right = controlItem?.querySelector('.glide__arrow--right');

            // Toggle disabled state based on position
            if (left) {
              left.classList.toggle('is-disabled', isAtStart);
              if (isDirectionRightToLeft) {
                left.classList.remove('glide__arrow--disabled');
              }
            }

            if (right) {
              right.classList.toggle('is-disabled', isAtEnd);
              if (isDirectionRightToLeft) {
                right.classList.remove('glide__arrow--disabled');
              }
            }
          }
        });
      },
    };
  };

// ============================================================================
// Main Component
// ============================================================================

/**
 * CobrandVanilaCarousel Component
 *
 * Performance optimizations:
 * - Memoized with React.memo for prop comparison
 * - useCallback for event handlers to prevent re-creation
 * - useMemo for computed values
 * - Efficient resize handling with cleanup
 * - Optimized Glide.js initialization
 *
 * Bug fixes:
 * - Fixed chevron positioning on mobile/tablet via proper CSS timing
 * - Proper cleanup of event listeners
 * - Fixed RTL arrow direction handling
 */
export const CobrandVanilaCarousel: FC<VanilaCardCarouselContainerProps> = memo<VanilaCardCarouselContainerProps>(
  props => {
    const {
      componentId,
      subHeaderText,
      styleclass,
      variation,
      customAttributes,
      eyebrow,
      cardCount,
      trackingProperties,
      children,
      ctaLabel,
      headerText,
      ctaLink,
      openInaNewTab,
      ctaType,
      noOfCards,
      noOfCardsTablet,
      enableTabletBreakpoint = true,
      isDirectionRightToLeft,
      layout,
      cardTag,
      isCombo,
      comboHeaderTag,
      standardHeaderTag,
      arrowPosition = 'side',
    } = props;

    // ========================================================================
    // State & Refs
    // ========================================================================

    const [cardsPerView, setCardsPerView] = useState<CardsPerView>(noOfCards);
    const leftArrowRef = useRef<HTMLButtonElement>(null);
    const rightArrowRef = useRef<HTMLButtonElement>(null);
    const leftArrowClickTrackRef = useRef<HTMLInputElement>(null);
    const rightArrowClickTrackRef = useRef<HTMLInputElement>(null);

    // ========================================================================
    // Viewport Detection
    // ========================================================================

    // Use useCheckBreakpoint to determine if viewport is desktop (>= 992px)
    // viewportL corresponds to 992px breakpoint - tablets and below get bottom arrows
    const isDesktop = useCheckBreakpoint('viewportL');

    // ========================================================================
    // Computed Values
    // ========================================================================

    const isLink = useMemo(() => ctaType === 'tertiaryLink', [ctaType]);
    const showNavigationControls = useMemo(() => cardCount > 0 && cardCount > cardsPerView, [cardCount, cardsPerView]);

    // ========================================================================
    // Event Handlers
    // ========================================================================

    /**
     * Handles CTA button click - opens link in specified target
     */
    const buttonCallback = useCallback(() => {
      if (!ctaLink) return;
      window.open(ctaLink, openInaNewTab ? '_blank' : '_self');
    }, [ctaLink, openInaNewTab]);

    /**
     * Handles window resize to adjust cards per view
     * Memoized to prevent recreation on every render
     */
    const handleResize = useCallback(() => {
      const viewportWidth = window.innerWidth;

      // Determine cards per view based on breakpoints
      if (viewportWidth >= BREAKPOINTS.LARGE_DESKTOP_MIN) {
        setCardsPerView(noOfCards);
      } else if (viewportWidth >= BREAKPOINTS.DESKTOP_MIN && viewportWidth < BREAKPOINTS.LARGE_DESKTOP_MIN) {
        setCardsPerView(noOfCardsTablet);
      } else if (
        enableTabletBreakpoint &&
        viewportWidth >= BREAKPOINTS.TABLET_MIN &&
        viewportWidth <= BREAKPOINTS.TABLET_MAX
      ) {
        setCardsPerView(noOfCardsTablet);
      } else {
        setCardsPerView(1);
      }
    }, [noOfCards, noOfCardsTablet, enableTabletBreakpoint]);

    /**
     * Adds click tracking to arrow buttons
     * Returns cleanup function for event listeners
     */
    const addClickTrackListeners = useCallback(
      (origRef: RefObject<HTMLElement>, clickTrackRef: RefObject<HTMLElement>): (() => void) => {
        const handler: EventListener = (e: Event) => {
          clickTrackRef.current?.click();
          e.preventDefault();
        };

        origRef.current?.addEventListener('touchstart', handler);
        origRef.current?.addEventListener('click', handler);

        // Return cleanup function
        return () => {
          origRef.current?.removeEventListener('touchstart', handler);
          origRef.current?.removeEventListener('click', handler);
        };
      },
      []
    );

    // ========================================================================
    // Effects
    // ========================================================================

    /**
     * Effect: Handle window resize events
     * Sets initial cards per view and listens for resize
     */
    useEffect(() => {
      handleResize(); // Set initial value
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [handleResize]);

    /**
     * Effect: Initialize Glide.js carousel
     * Handles carousel setup, arrow tracking, and cleanup
     *
     * Bug Fix: Added requestAnimationFrame to ensure DOM is ready
     * This fixes the chevron positioning issue on mobile/tablet
     */
    useEffect(() => {
      const carouselRootSelector = `#${componentId}`;
      const carouselElement = document?.querySelector(carouselRootSelector);

      if (!carouselElement) return;

      // Use requestAnimationFrame to ensure DOM is fully rendered
      // This fixes the chevron positioning bug on initial load
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let glideInstance: any;
      let cleanupLeftArrow: (() => void) | undefined;
      let cleanupRightArrow: (() => void) | undefined;

      const rafId = requestAnimationFrame(() => {
        try {
          glideInstance = new Glide(carouselRootSelector, {
            type: GLIDE_CONFIG.TYPE,
            bound: GLIDE_CONFIG.BOUND,
            perView: noOfCards,
            startAt: GLIDE_CONFIG.START_AT,
            rewind: GLIDE_CONFIG.REWIND,
            direction: isDirectionRightToLeft ? 'rtl' : 'ltr',
            gap: GLIDE_CONFIG.GAP,
            dragThreshold: GLIDE_CONFIG.DRAG_THRESHOLD,
            breakpoints: enableTabletBreakpoint
              ? {
                  [BREAKPOINTS.LARGE_DESKTOP_MIN - 1]: {
                    perView: noOfCardsTablet,
                  },
                  [BREAKPOINTS.TABLET_MAX]: {
                    perView: noOfCardsTablet,
                    gap: GLIDE_CONFIG.GAP_MOBILE,
                  },
                  [BREAKPOINTS.MOBILE_MAX]: {
                    perView: 1,
                  },
                }
              : {
                  [BREAKPOINTS.LARGE_DESKTOP_MIN - 1]: {
                    perView: noOfCardsTablet,
                  },
                  [BREAKPOINTS.TABLET_MAX]: {
                    perView: 1,
                    gap: GLIDE_CONFIG.GAP_MOBILE,
                  },
                },
          });

          // Mount with arrow disabler extension
          glideInstance.mount({ ArrowDisabler: createArrowDisabler(isDirectionRightToLeft ?? false) });

          // Setup click tracking for arrows
          cleanupLeftArrow = addClickTrackListeners(leftArrowRef, leftArrowClickTrackRef);
          cleanupRightArrow = addClickTrackListeners(rightArrowRef, rightArrowClickTrackRef);
        } catch (error) {
          console.error(`Error initializing Glide carousel for ${componentId}:`, error);
        }
      });

      // Return cleanup function
      return () => {
        cancelAnimationFrame(rafId);
        if (cleanupLeftArrow) cleanupLeftArrow();
        if (cleanupRightArrow) cleanupRightArrow();
        if (glideInstance?.destroy) glideInstance.destroy();
      };
    }, [
      componentId,
      noOfCards,
      noOfCardsTablet,
      enableTabletBreakpoint,
      isDirectionRightToLeft,
      cardsPerView,
      addClickTrackListeners,
    ]);

    // ========================================================================
    // Render Functions
    // ========================================================================

    /**
     * Renders eyebrow text component
     */
    const renderEyebrow = () => (eyebrow ? <Eyebrow text={eyebrow} /> : null);

    /**
     * Renders header text component
     */
    const renderHeader = () =>
      headerText?.trim().length ? (
        <Heading
          customClass={clsx({ 'mt-2 mb-3': isCombo }, isCombo ? 'm-ellipsis-3lines' : 'm-ellipsis-2lines')}
          titleText={headerText}
          variation={headingType.title}
          fontSize={isCombo ? size.medium : size.small}
          element={isCombo ? comboHeaderTag : standardHeaderTag}
        />
      ) : null;

    /**
     * Renders subheader text component
     */
    const renderSubHeader = () =>
      subHeaderText ? (
        <Text
          copyText={subHeaderText}
          fontSize={size.medium}
          element={tags.span}
          customClass={clsx({ 'mb-3': isCombo || eyebrow }, isCombo ? 'm-ellipsis-3lines' : 'm-ellipsis-2lines')}
        />
      ) : null;

    /**
     * Renders CTA button component
     */
    const renderCta = () =>
      ctaLabel ? (
        <div className={clsx(!eyebrow && !subHeaderText ? 'carousal_header--cta mb-2' : 'carousal_header--cta')}>
          <MButton
            ctaLink={isLink ? ctaLink : undefined}
            onClick={!isLink ? buttonCallback : undefined}
            buttonType={
              ctaType === 'primaryButton'
                ? MButtonType.Primary
                : ctaType === 'secondaryButton'
                ? MButtonType.Secondary
                : MButtonType.Tertiary
            }
            openInANewTab={isLink && openInaNewTab}
            iconPlacement={openInaNewTab ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
            custom_click_track_value={`${trackingProperties?.cardLocation}|${trackingProperties?.description}|${
              openInaNewTab ? 'external' : 'internal'
            }`}
            buttonText={ctaLabel}
          />
        </div>
      ) : null;

    /**
     * Renders carousel header
     */
    const renderCarouselHeader = () => (
      <div className={clsx({ 'pr-5': isCombo }, 'carousal_header')}>
        <div className={!isCombo && eyebrow ? 'pb-2' : ''}>
          {renderEyebrow()}
          {renderHeader()}
          {renderSubHeader()}
        </div>
        {renderCta()}
      </div>
    );
    /**
     * Renders navigation controls (arrows and pagination)
     */
    const renderNavigationControls = () => {
      // Determine carousel control class:
      // - CardOverview (arrowPosition === 'bottom'): always use carouselControlType3b
      // - Card carousels (arrowPosition === 'side'): use carouselControlType1 for desktop, carouselControlType3b for mobile/tablet
      const getCarouselControlClass = () => {
        if (arrowPosition === 'bottom') {
          return 'carouselControlType3b';
        }
        return isDesktop ? 'carouselControlType1' : 'carouselControlType3b';
      };

      return showNavigationControls ? (
        <div className="center-align mt-3 mt-lg-0">
          <div className={getCarouselControlClass()}>
            {/* Left Arrow */}
            <div className="glide__arrows" data-glide-el="controls">
              <button
                className={clsx('glide__arrow--left', `${isDirectionRightToLeft ? 'right-arrow' : 'left-arrow'}`)}
                id={`${componentId}_left_arrow`}
                ref={leftArrowRef}
                data-glide-dir={isDirectionRightToLeft ? '>' : '<'}
                aria-label="left-arrow"
              >
                <span
                  className={isDirectionRightToLeft ? 'icon-arrow-right' : 'icon-arrow-left'}
                  id={`${componentId}_left_arrow_icon`}
                  data-id="left"
                />
              </button>
            </div>

            {/* Left Arrow Tracking */}
            <input
              type="hidden"
              id={`${componentId}-left-arrow-click-track`}
              ref={leftArrowClickTrackRef}
              className={clsx(`${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`)}
              data-custom_click_track_value={`${trackingProperties?.cardLocation}|${trackingProperties?.leftArrowDesc}|internal`}
            />

            {/* Pagination Dots */}
            <div data-glide-el="controls[nav]">
              {Array.from({ length: Math.max(1, cardCount - (cardsPerView - 1)) }, (_, i) => (
                <button className="" data-glide-dir={i} aria-label={`page-${i + 1}`} key={cardsPerView + i}></button>
              ))}
            </div>

            {/* Right Arrow */}
            <div className="glide__arrows" data-glide-el="controls">
              <button
                className={clsx('glide__arrow--right', `${isDirectionRightToLeft ? 'left-arrow' : 'right-arrow'}`)}
                id={`${componentId}_right_arrow`}
                ref={rightArrowRef}
                data-glide-dir={isDirectionRightToLeft ? '<' : '>'}
                aria-label="right-arrow"
              >
                <span
                  className={isDirectionRightToLeft ? 'icon-arrow-left' : 'icon-arrow-right'}
                  id={`${componentId}_right_arrow_icon`}
                  data-id="right"
                />
              </button>
            </div>

            {/* Right Arrow Tracking */}
            <input
              type="hidden"
              id={`${componentId}-right-arrow-click-track`}
              ref={rightArrowClickTrackRef}
              className={clsx(`${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`)}
              data-custom_click_track_value={`${trackingProperties?.cardLocation}|${trackingProperties?.rightArrowDesc}|internal`}
            />
          </div>
        </div>
      ) : null;
    };

    /**
     * Renders card tag for 5-card layout
     */
    const renderCardTag = () =>
      layout === '5-card' ? (
        <div className="pb-3">
          <span className="card-tag">{cardTag || 'Personal'}</span>
        </div>
      ) : null;

    // ========================================================================
    // Main Render
    // ========================================================================

    return (
      <StyledCardCarousel
        data-component-name="m-cobrand-CobrandVanilaCarousel"
        data-testid="cobrand-CobrandVanilaCarousel"
        className={clsx(`col-12 px-0 ${styleclass || ''}`, layout === '5-card' ? '' : 'py-3')}
        {...customAttributes}
        variation={variation}
      >
        {renderCarouselHeader()}

        <div id={componentId} className={clsx(`glide ${isCombo ? 'col-xl-8 p-0' : ''}`)}>
          <div className={clsx(layout === '5-card' ? 'personal-wrapper px-lg-4' : '')}>
            {renderCardTag()}

            <div
              className={clsx(!subHeaderText && !eyebrow ? 'glide__track__pt3' : 'glide__track')}
              data-glide-el="track"
            >
              <ul className={clsx('glide__slides', 'pb-3')} id={`${componentId}__slides`}>
                {children}
              </ul>
            </div>
          </div>

          {renderNavigationControls()}
        </div>
      </StyledCardCarousel>
    );
  }
);

CobrandVanilaCarousel.displayName = 'CobrandVanilaCarousel';
